import { DOCUMENT, inject, Injectable } from '@angular/core';
import { ENVIRONMENT } from '@env/environment.token';
import { IpBlacklistService } from '@services/ipBlacklist.service';

type FbqFn = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  loaded: boolean;
  version: string;
  push: unknown;
};

type GtagFn = (...args: unknown[]) => void;

declare global {
  interface Window {
    fbq?: FbqFn;
    _fbq?: FbqFn;
    gtag?: GtagFn;
    dataLayer?: unknown[];
    esSdk?: string;
    es?: ((...args: unknown[]) => void) & { q?: unknown[] };
    adsbygoogle?: unknown[];
  }
}

const GTAG_SRC = 'https://www.googletagmanager.com/gtag/js';
const GTM_SRC = 'https://www.googletagmanager.com/gtm.js';
const FB_EVENTS_SRC = 'https://connect.facebook.net/en_US/fbevents.js';
const ESPUTNIK_SRC = 'https://esputnik.com/scripts/v1/public/scripts';
const ADSENSE_SRC = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js';

@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private readonly document = inject(DOCUMENT);
  private readonly env = inject(ENVIRONMENT);
  private readonly config = this.env.analytics;
  private readonly ipBlacklist = inject(IpBlacklistService);

  private get window(): Window {
    return this.document.defaultView as Window;
  }

  initAll(): void {
    this.stopIfFramed();
    this.initGtag();
    this.initGtm();
    this.initFacebookPixel();
    this.initEsputnik();
    this.initAdSense();
  }

  stopIfFramed(): void {
    if (this.window.parent !== this.window) {
      this.window.stop?.();
    }
  }

  initGtag(): void {
    const id = this.config?.gaMeasurementId;
    if (!id || this.window.gtag) {
      return;
    }

    this.window.dataLayer = this.window.dataLayer ?? [];

    const gtag: GtagFn = (...args: unknown[]) => {
      this.window.dataLayer!.push(args);
    };
    this.window.gtag = gtag;

    this.loadScript(`${GTAG_SRC}?id=${id}`);

    gtag('js', new Date());
    gtag('config', id);
  }

  initFacebookPixel(): void {
    const ids = this.config?.facebookPixelIds;
    if (!ids?.length || this.window.fbq) {
      return;
    }

    const fbq = ((...args: unknown[]) => {
      fbq.callMethod ? fbq.callMethod.apply(fbq, args) : fbq.queue.push(args);
    }) as FbqFn;

    fbq.queue = [];
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.push = fbq;

    this.window.fbq = fbq;
    this.window._fbq = fbq;

    this.loadScript(FB_EVENTS_SRC);

    ids.forEach((id) => fbq('init', id));
    fbq('track', 'PageView');
  }

  initGtm(): void {
    const id = this.config?.gtmId;
    if (!id || this.isLoaded(GTM_SRC)) {
      return;
    }

    this.window.dataLayer = this.window.dataLayer ?? [];
    this.window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });

    this.loadScript(`${GTM_SRC}?id=${id}`);
  }

  initEsputnik(): void {
    const { apiKey, domain } = this.config?.esputnik ?? {};
    if (!apiKey || !domain || this.window.es) {
      return;
    }

    const es = ((...args: unknown[]) => {
      es.q = es.q ?? [];
      es.q.push(args);
    }) as NonNullable<Window['es']>;

    this.window.esSdk = 'es';
    this.window.es = es;

    this.loadScript(`${ESPUTNIK_SRC}?apiKey=${apiKey}&domain=${domain}`);

    es('pushOn');
  }

  initAdSense(): void {
    const client = this.config?.adsenseClient;
    if (!client || this.isLoaded(ADSENSE_SRC)) {
      return;
    }

    if (!this.ipBlacklist.adsAllowed()) {
      return;
    }

    this.loadScript(`${ADSENSE_SRC}?client=${client}`, { crossOrigin: 'anonymous' });
  }

  trackPageView(): void {
    this.window.fbq?.('track', 'PageView');
    this.window.dataLayer?.push({ event: 'page_view' });
  }

  trackEvent(name: string, payload: Record<string, unknown> = {}): void {
    this.window.fbq?.('trackCustom', name, payload);
    this.window.dataLayer?.push({ event: name, ...payload });
  }

  private loadScript(src: string, attrs: Partial<HTMLScriptElement> = {}): void {
    const script = this.document.createElement('script');
    script.src = src;
    script.async = true;
    Object.assign(script, attrs);
    this.document.head.appendChild(script);
  }

  private isLoaded(srcPrefix: string): boolean {
    return !!this.document.querySelector(`script[src^="${srcPrefix}"]`);
  }
}
